import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { VNG_DIALOG_DATA, VngDialogRef, VngSelectOption } from "@vengreso/angular-components";
import { find } from "lodash";
import { Subscription } from "rxjs";
import { getCategoriesReward, getPlanId } from "src/app/core/core-services/constants/plans.constants";
import { NotifyService } from "src/app/core/menu/not.service";

@Component({
    selector: "app-category-rename",
    templateUrl: "./category-rename.component.html",
    styleUrls: ["./category-rename.component.scss"],
    standalone: false
})
export class CategoryRenameComponent implements OnInit, OnDestroy {
  form: FormGroup;
  id: any;
  categoryErrMsg: any;
  subCategoryErrMsg: any;
  isSelectDisable: any;
  filterCtrlSubscription: Subscription;

  constructor(
    public dialogRef: VngDialogRef<CategoryRenameComponent>,
    public notifyService: NotifyService,
    private cdr: ChangeDetectorRef,
    @Inject(VNG_DIALOG_DATA) public data: any,
    private fb: FormBuilder
  ) {
    this.form = this.fb.group({
      name: [this.data.data.name ? this.data.data.name : null, [Validators.required, Validators.maxLength(26)]],
      category_id: [this.data.data.category_id ? this.data.data.category_id : null],
    });
    this.isSelectDisable = new FormControl(this.data.data.category_id != null);
    // L2: the parent select only participates while "Nest … under:" is
    // checked. Driven from here (not a template [disabled] binding) because
    // [formControl] + [disabled] collide — the CVA's setDisabledState()
    // overwrites the @Input right after writeValue() at init.
    if (!this.isSelectDisable.value) {
      this.form.get("category_id").disable({ emitEvent: false });
    }
  }

  categories: any[] = [];
  // R5: mat-select + the old select-search plugin -> vng-select [searchable]="true".
  parentCategoryOptions: VngSelectOption[] = [];

  ngOnInit(): void {
    this.id = this.data.data.id;
    this.categories = [...(this.data.self.shortcutCategoryList || this.data.self.categoryList)];
    let planId = getPlanId();
    let subCategoriesCount = this.countSubCategories();
    let additionalCategoryReward = getCategoriesReward();
    if (planId == "Free" && this.categories.length >= 7 + additionalCategoryReward) {
      this.categoryErrMsg = [
        `Exceeds the limit of ${7 + additionalCategoryReward} categories.`,
        `Reassign to an existing category or upgrade plan. `,
      ];
    }
    if (planId == "Starter" && this.categories.length >= 14 + additionalCategoryReward) {
      this.categoryErrMsg = [
        `Exceeds the limit of ${14 + additionalCategoryReward} categories.`,
        `Reassign to an existing category or upgrade plan. `,
      ];
    }
    if (planId == "Free" && subCategoriesCount >= 14) {
      this.subCategoryErrMsg = [
        `Exceeds the limit of 14 subcategories.`,
        `Reassign to an existing subcategory or upgrade plan. `,
      ];
    }
    if (planId == "Starter" && subCategoriesCount >= 28) {
      this.subCategoryErrMsg = [
        `Exceeds the limit of 28 subcategories.`,
        `Reassign to an existing subcategory or upgrade plan. `,
      ];
    }
    this.buildParentCategoryOptions();
  }

  countSubCategories(): number {
    let count = 0;
    for (const item of this.categories) {
      count += item.sub_category_lv1.length;
    }
    return count;
  }

  /**
   * "Nest … under:" toggled. VngCheckbox writes the new value into
   * `isSelectDisable` before emitting `change`, so the control is already
   * up to date here.
   */
  onNestChange(): void {
    const parent = this.form.get("category_id");
    if (this.isSelectDisable.value) {
      parent.enable();
    } else {
      parent.setValue(null);
      parent.disable();
    }
  }

  tooltip(element: any): string {
    return `${element.name1 || element.name2 ? "Sub-" : ""}Category`;
  }

  // R5: flatten categories + sub_category_lv1 into data-driven vng-select
  // options once — vng-select's own [searchable]="true" substring search
  // now does the filtering filterCategories() used to do by hand.
  buildParentCategoryOptions(): void {
    const options: VngSelectOption[] = [];
    for (const cat of this.categories || []) {
      options.push({ value: cat.id, label: cat.name });
      for (const sub1 of cat.sub_category_lv1 || []) {
        options.push({ value: sub1.id, label: "\u00A0\u00A0\u00A0\u00A0\u00A0" + sub1.name });
      }
    }
    this.parentCategoryOptions = options;
  }

  // R3: field errors are only painted once a control is dirty/touched, so the
  // submit path has to mark the whole form as touched — otherwise pressing
  // Save/Update on an untouched form would silently do nothing.
  submitForm(): void {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      // getRawValue(): category_id is disabled while "Nest … under:" is
      // unchecked, and form.value drops disabled controls — the payload must
      // still carry the (null) key exactly as it did before.
      this.updateCategory(this.form.getRawValue());
    }
  }

  updateCategory(value) {
    let item = find(this.categories, { id: value.category_id });
    this.data.data.destination_category_type = "category";

    if (!item && this.form && this.form.get("category_id")) {
      for (const category of this.categories) {
        item = find(category.sub_category_lv1, { id: value.category_id });

        if (item) {
          this.data.data.destination_category_type = "sub_categories_lv1";
          value.sub_categories_lv1 = value.category_id;
          value.category_id = item.category_id;
          break;
        }
      }
    }

    if (this.id) {
      if (item && item.id) this.data.data.destination_category_id = item.id;
      this.data.data.name = value.name;
      this.data.self.categoryUpdate(this.data.data);
    } else {
      if (!value.category_id) delete value.category_id;
      this.data.self.saveCategory(value);
    }
    this.notifyService.notifyAboutChange();
    this.cdr.detectChanges();
    this.close();
  }

  close(): void {
    this.dialogRef.close();
    if (this.filterCtrlSubscription) {
      this.filterCtrlSubscription.unsubscribe();
    }
  }

  ngOnDestroy(): void {
    if (this.filterCtrlSubscription) {
      this.filterCtrlSubscription.unsubscribe();
    }
  }
}
